/**
 * TaskListConfigModal - Large configuration modal for task list widgets
 *
 * Supports two modes:
 * - Simple: UI-based filtering with checkboxes and dropdowns
 * - Advanced: Direct query text editing for complex filters
 */

import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Filter, LayoutGrid, SlidersHorizontal, Code, Sparkles } from 'lucide-react';
import { TaskStatus } from '../../types';
import type { TaskListWidgetConfig, TaskDisplayField } from '../../types/widgets';
import { useTodos } from '../../hooks/useTodos';
import {
  buildQueryFromUI,
  queryToText,
  parseQueryText,
  QUERY_PRESETS,
} from '../../utils/queryBuilder';
import { executeQuery } from '../../utils/queryEngine';

interface TaskListConfigModalProps {
  config: TaskListWidgetConfig;
  onSave: (config: TaskListWidgetConfig) => void;
  onClose: () => void;
}

const DATE_FILTER_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This week' },
  { value: 'this-month', label: 'This month' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'no-date', label: 'No date set' },
  { value: 'has-date', label: 'Has date' },
] as const;

type DateFilterValue = (typeof DATE_FILTER_OPTIONS)[number]['value'];

const STATUS_OPTIONS = [
  { value: TaskStatus.NOT_STARTED, label: 'Not Started', color: 'bg-gray-400' },
  { value: TaskStatus.IN_PROGRESS, label: 'In Progress', color: 'bg-blue-500' },
  { value: TaskStatus.COMPLETED, label: 'Completed', color: 'bg-green-500' },
  { value: TaskStatus.WAITING, label: 'Waiting', color: 'bg-yellow-500' },
  { value: TaskStatus.CANCELED, label: 'Canceled', color: 'bg-red-400' },
] as const;

const HORIZON_OPTIONS = [
  { value: 'now', label: 'Now', color: 'bg-red-500' },
  { value: 'next', label: 'Next', color: 'bg-orange-500' },
  { value: 'soon', label: 'Soon', color: 'bg-yellow-500' },
  { value: 'later', label: 'Later', color: 'bg-blue-500' },
  { value: 'someday', label: 'Someday', color: 'bg-gray-400' },
] as const;

const DISPLAY_FIELD_OPTIONS: { value: TaskDisplayField; label: string; description: string }[] = [
  { value: 'status', label: 'Status icon', description: 'Clickable status checkbox' },
  { value: 'horizon', label: 'Horizon', description: 'Horizon badge' },
  { value: 'date', label: 'Todo date', description: 'Scheduled date' },
  { value: 'deadline', label: 'Deadline', description: 'Due date with overdue warning' },
  { value: 'tags', label: 'Tags', description: 'Task tags/labels' },
  { value: 'description', label: 'Description', description: 'Preview of description' },
  { value: 'assignee', label: 'Assignee', description: 'Assigned team member' },
];

const SORT_OPTIONS = [
  { value: 'date', label: 'Todo date' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'horizon', label: 'Horizon' },
  { value: 'created', label: 'Created date' },
  { value: 'title', label: 'Title (A-Z)' },
] as const;

const GROUP_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'status', label: 'By status' },
  { value: 'date', label: 'By date' },
  { value: 'horizon', label: 'By horizon' },
] as const;

export function TaskListConfigModal({ config, onSave, onClose }: TaskListConfigModalProps) {
  const [localConfig, setLocalConfig] = useState<TaskListWidgetConfig>({
    title: config.title || '',
    queryMode: config.queryMode || 'simple',
    query: config.query,
    queryText: config.queryText || '',
    statusFilter: config.statusFilter || [],
    horizonFilter: [] as string[],
    tagsFilter: config.tagsFilter || [],
    dateFilter: config.dateFilter || 'all',
    dateFilters: config.dateFilters || [],
    showCompleted: config.showCompleted ?? true,
    onlyWithDeadline: config.onlyWithDeadline ?? false,
    onlyOverdue: config.onlyOverdue ?? false,
    displayFields: config.displayFields || ['status', 'date', 'horizon'],
    showTaskCount: config.showTaskCount ?? true,
    compactMode: config.compactMode ?? false,
    groupBy: config.groupBy || 'none',
    sortBy: config.sortBy || 'date',
    sortDirection: config.sortDirection || 'asc',
    maxItems: config.maxItems,
  });

  const [queryError, setQueryError] = useState<string | null>(null);

  const { allTags, todos } = useTodos();

  // Generate query preview from simple mode options
  const generatedQueryText = useMemo(() => {
    if (localConfig.queryMode === 'advanced') return localConfig.queryText || '';

    const query = buildQueryFromUI({
      statusFilter: localConfig.statusFilter,
      horizonFilter: localConfig.horizonFilter,
      tagsFilter: localConfig.tagsFilter,

      dateFilter: localConfig.dateFilters?.length ? undefined : localConfig.dateFilter,
      dateFilters: localConfig.dateFilters,
      showCompleted: localConfig.showCompleted,
      onlyWithDeadline: localConfig.onlyWithDeadline,
      onlyOverdue: localConfig.onlyOverdue,
    });
    return queryToText(query);
  }, [localConfig]);

  // Count matching tasks
  const matchCount = useMemo(() => {
    let query;
    if (localConfig.queryMode === 'advanced' && localConfig.queryText) {
      query = parseQueryText(localConfig.queryText);
    } else {
      query = buildQueryFromUI({
        statusFilter: localConfig.statusFilter,
        horizonFilter: localConfig.horizonFilter,
        tagsFilter: localConfig.tagsFilter,
  
        dateFilter: localConfig.dateFilters?.length ? undefined : localConfig.dateFilter,
        dateFilters: localConfig.dateFilters,
        showCompleted: localConfig.showCompleted,
        onlyWithDeadline: localConfig.onlyWithDeadline,
        onlyOverdue: localConfig.onlyOverdue,
      });
    }

    return executeQuery(todos, query).length;
  }, [todos, localConfig]);

  const toggleArrayItem = <T,>(array: T[] | undefined, item: T): T[] => {
    const arr = array || [];
    return arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];
  };

  const handleQueryTextChange = (text: string) => {
    setLocalConfig({ ...localConfig, queryText: text });
    // Validate the query
    const parsed = parseQueryText(text);
    if (text && !parsed) {
      setQueryError('Invalid query syntax');
    } else {
      setQueryError(null);
      if (parsed) {
        setLocalConfig(prev => ({ ...prev, queryText: text, query: parsed }));
      }
    }
  };

  const handleApplyPreset = (presetKey: string) => {
    const preset = QUERY_PRESETS[presetKey];
    if (preset) {
      const text = queryToText(preset.query);
      setLocalConfig({
        ...localConfig,
        queryMode: 'advanced',
        query: preset.query,
        queryText: text,
        title: localConfig.title || preset.name,
      });
      setQueryError(null);
    }
  };

  const handleSwitchToAdvanced = () => {
    // Convert current simple config to query text
    const query = buildQueryFromUI({
      statusFilter: localConfig.statusFilter,
      horizonFilter: localConfig.horizonFilter,
      tagsFilter: localConfig.tagsFilter,

      dateFilter: localConfig.dateFilters?.length ? undefined : localConfig.dateFilter,
      dateFilters: localConfig.dateFilters,
      showCompleted: localConfig.showCompleted,
      onlyWithDeadline: localConfig.onlyWithDeadline,
      onlyOverdue: localConfig.onlyOverdue,
    });
    setLocalConfig({
      ...localConfig,
      queryMode: 'advanced',
      query,
      queryText: queryToText(query),
    });
  };

  const handleSave = () => {
    // In advanced mode, parse the query text to ensure it's valid
    if (localConfig.queryMode === 'advanced' && localConfig.queryText) {
      const parsed = parseQueryText(localConfig.queryText);
      if (!parsed && localConfig.queryText.trim()) {
        setQueryError('Please fix the query syntax before saving');
        return;
      }
      onSave({ ...localConfig, query: parsed });
    } else {
      onSave(localConfig);
    }
    onClose();
  };

  const isAdvancedMode = localConfig.queryMode === 'advanced';

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              Configure Task List Widget
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {matchCount} tasks match current filters
            </p>
          </div>

          {/* Mode Toggle */}
          <div className="flex items-center gap-2 mr-4">
            <button
              onClick={() => setLocalConfig({ ...localConfig, queryMode: 'simple' })}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-l-lg border transition-colors ${
                !isAdvancedMode
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600'
              }`}
            >
              <Filter className="w-4 h-4" />
              Simple
            </button>
            <button
              onClick={handleSwitchToAdvanced}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-r-lg border transition-colors ${
                isAdvancedMode
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600'
              }`}
            >
              <Code className="w-4 h-4" />
              Advanced
            </button>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left Column - Filters */}
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  Widget Title
                </label>
                <input
                  type="text"
                  value={localConfig.title || ''}
                  onChange={(e) => setLocalConfig({ ...localConfig, title: e.target.value })}
                  placeholder="e.g., Today's Tasks, High Priority, etc."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {isAdvancedMode ? (
                /* Advanced Mode - Query Editor */
                <div className="space-y-4">
                  {/* Presets */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                      <Sparkles className="w-4 h-4 inline mr-1" />
                      Quick Presets
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(QUERY_PRESETS).map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => handleApplyPreset(key)}
                          className="px-3 py-1.5 text-xs rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title={preset.description}
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Query Editor */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                        <Code className="w-5 h-5" />
                        <h3 className="font-semibold">Query</h3>
                      </div>
                      {queryError && (
                        <span className="text-xs text-red-500">{queryError}</span>
                      )}
                    </div>

                    <textarea
                      value={localConfig.queryText || ''}
                      onChange={(e) => handleQueryTextChange(e.target.value)}
                      placeholder="status IN (not_started, in_progress) AND horizon = now"
                      className={`w-full h-32 px-4 py-3 font-mono text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                        queryError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />

                    {/* Syntax Help */}
                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                      <p className="font-medium">Syntax:</p>
                      <p><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">field = value</code> equals</p>
                      <p><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">field IN (a, b, c)</code> any of values</p>
                      <p><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">field &gt;= $today</code> date comparison</p>
                      <p><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">tags HAS ANY (tag1, tag2)</code> array contains</p>
                      <p><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">field IS NULL</code> empty check</p>
                      <p>Combine with <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">AND</code> / <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">OR</code></p>
                      <p className="mt-2 font-medium">Date expressions: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">$today</code> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">$tomorrow</code> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">$startOfWeek</code> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">$endOfMonth</code> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">$daysFromNow(7)</code></p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Simple Mode - UI Filters */
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                      <Filter className="w-5 h-5" />
                      <h3 className="font-semibold">Filters</h3>
                    </div>
                  </div>

                  {/* Date Filter (multi-select, OR logic) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Date Range <span className="text-gray-400 font-normal">(empty = all, multiple = OR)</span>
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {DATE_FILTER_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() =>
                            setLocalConfig({
                              ...localConfig,
                              dateFilters: toggleArrayItem(localConfig.dateFilters, opt.value as DateFilterValue),
                              dateFilter: undefined,
                            })
                          }
                          className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                            localConfig.dateFilters?.includes(opt.value as DateFilterValue)
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status <span className="text-gray-400 font-normal">(empty = all)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() =>
                            setLocalConfig({
                              ...localConfig,
                              statusFilter: toggleArrayItem(localConfig.statusFilter, opt.value),
                            })
                          }
                          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                            localConfig.statusFilter?.includes(opt.value)
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${opt.color}`} />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Horizon Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Horizon <span className="text-gray-400 font-normal">(empty = all)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {HORIZON_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() =>
                            setLocalConfig({
                              ...localConfig,
                              horizonFilter: toggleArrayItem(localConfig.horizonFilter, opt.value),
                            })
                          }
                          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                            localConfig.horizonFilter?.includes(opt.value)
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                          }`}
                        >
                          <span className={`w-3 h-3 rounded-full ${opt.color}`} />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tags Filter */}
                  {allTags.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tags <span className="text-gray-400 font-normal">(empty = all)</span>
                      </label>
                      <div className="flex flex-wrap gap-2 max-h-24 overflow-auto">
                        {allTags.map((tag) => (
                          <button
                            key={tag}
                            onClick={() =>
                              setLocalConfig({
                                ...localConfig,
                                tagsFilter: toggleArrayItem(localConfig.tagsFilter, tag),
                              })
                            }
                            className={`px-3 py-1.5 text-sm rounded-full border-2 transition-all ${
                              localConfig.tagsFilter?.includes(tag)
                                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                            }`}
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Filter Checkboxes */}
                  <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={localConfig.showCompleted ?? true}
                        onChange={(e) => setLocalConfig({ ...localConfig, showCompleted: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                        Show completed tasks
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={localConfig.onlyWithDeadline ?? false}
                        onChange={(e) => setLocalConfig({ ...localConfig, onlyWithDeadline: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                        Only tasks with deadline
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={localConfig.onlyOverdue ?? false}
                        onChange={(e) => setLocalConfig({ ...localConfig, onlyOverdue: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                        Only overdue tasks
                      </span>
                    </label>
                  </div>

                  {/* Generated Query Preview */}
                  {generatedQueryText && (
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Generated query:</p>
                      <code className="block text-xs bg-gray-200 dark:bg-gray-700 p-2 rounded text-gray-700 dark:text-gray-300 overflow-x-auto">
                        {generatedQueryText}
                      </code>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column - Display Options */}
            <div className="space-y-6">
              {/* Display Fields */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                  <LayoutGrid className="w-5 h-5" />
                  <h3 className="font-semibold">Display Fields</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Choose which information to show for each task
                </p>

                <div className="space-y-2">
                  {DISPLAY_FIELD_OPTIONS.map((field) => (
                    <label
                      key={field.value}
                      className="flex items-start gap-3 p-3 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={localConfig.displayFields?.includes(field.value) ?? false}
                        onChange={() =>
                          setLocalConfig({
                            ...localConfig,
                            displayFields: toggleArrayItem(localConfig.displayFields, field.value),
                          })
                        }
                        className="w-5 h-5 mt-0.5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {field.label}
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{field.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sorting & Layout */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                  <SlidersHorizontal className="w-5 h-5" />
                  <h3 className="font-semibold">Sorting & Layout</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sort by
                    </label>
                    <select
                      value={localConfig.sortBy || 'date'}
                      onChange={(e) =>
                        setLocalConfig({ ...localConfig, sortBy: e.target.value as TaskListWidgetConfig['sortBy'] })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {SORT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Group by
                    </label>
                    <select
                      value={localConfig.groupBy || 'none'}
                      onChange={(e) =>
                        setLocalConfig({ ...localConfig, groupBy: e.target.value as TaskListWidgetConfig['groupBy'] })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {GROUP_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max items <span className="text-gray-400 font-normal">(0 = unlimited)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={localConfig.maxItems || 0}
                    onChange={(e) => setLocalConfig({ ...localConfig, maxItems: parseInt(e.target.value) || undefined })}
                    className="w-32 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={localConfig.showTaskCount ?? true}
                      onChange={(e) => setLocalConfig({ ...localConfig, showTaskCount: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                      Show task count in header
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={localConfig.compactMode ?? false}
                      onChange={(e) => setLocalConfig({ ...localConfig, compactMode: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                      Compact mode (smaller items)
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isAdvancedMode ? 'Advanced mode: Edit query directly' : 'Simple mode: Use filters above'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
            >
              <Check className="w-4 h-4" />
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
